import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import ConversationList from "./ConversationList";
import MessageThread from "./MessageThread";
import NewConversationDialog from "./NewConversationDialog";
import { getConversations, getAllConversations, markConversationRead } from "../../api/conversationApi";
import { useSocket } from "../../common/contexts/SocketContext";
import { useAuth } from "../../common/contexts/AuthContext";
import "./Messages.scss";

const OVERSIGHT_ROLES = ["Admin", "Management", "SuperAdmin"];

const MessagesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get("c") ? Number(searchParams.get("c")) : null;
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(initialId);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [oversight, setOversight] = useState(false);
  const { socket } = useSocket();
  const { user } = useAuth();

  const effectiveRole = user?.roleName || user?.role?.name || user?.Role?.name;
  const canOversee = OVERSIGHT_ROLES.includes(effectiveRole);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = oversight ? await getAllConversations() : await getConversations();
      if (data?.success) setConversations(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Switching oversight mode changes which list we load and clears the thread.
    setActiveId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oversight]);

  // Refresh conversation list when a new message arrives anywhere
  useEffect(() => {
    if (!socket) return undefined;
    const onMessageNew = () => {
      refresh();
    };
    socket.on("message:new", onMessageNew);
    return () => {
      socket.off("message:new", onMessageNew);
    };
  }, [socket]);

  const handleSelect = async (conv) => {
    setActiveId(conv.id);
    setSearchParams({ c: String(conv.id) });
    // In oversight mode the admin isn't a participant — don't mark-read.
    if (oversight) return;
    try {
      await markConversationRead(conv.id);
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0 } : c))
      );
    } catch (e) {
      // silent
    }
  };

  const handleConversationCreated = (conv) => {
    setShowNewDialog(false);
    refresh();
    setActiveId(conv.id);
    setSearchParams({ c: String(conv.id) });
  };

  return (
    <div className="messages-page">
      <div className="messages-sidebar">
        <div className="messages-sidebar-header">
          <h3>{oversight ? "All conversations" : "Messages"}</h3>
          <div className="messages-sidebar-actions">
            {canOversee && (
              <Button
                icon={oversight ? "pi pi-eye" : "pi pi-eye-slash"}
                className={`p-button-rounded p-button-text ${oversight ? "p-button-warning" : ""}`}
                tooltip={oversight ? "Exit oversight" : "Oversight: view all conversations"}
                tooltipOptions={{ position: "left" }}
                onClick={() => setOversight((v) => !v)}
              />
            )}
            {!oversight && (
              <Button
                icon="pi pi-plus"
                className="p-button-rounded p-button-text"
                tooltip="New conversation"
                tooltipOptions={{ position: "left" }}
                onClick={() => setShowNewDialog(true)}
              />
            )}
          </div>
        </div>
        {oversight && (
          <div className="messages-oversight-banner">
            <i className="pi pi-info-circle" /> Oversight mode — viewing all school
            conversations as an administrator. Access is logged.
          </div>
        )}
        <ConversationList
          conversations={conversations}
          activeId={activeId}
          onSelect={handleSelect}
          loading={loading}
        />
      </div>
      <div className="messages-thread-container">
        {activeId ? (
          <MessageThread
            conversationId={activeId}
            conversation={conversations.find((c) => c.id === activeId)}
            onMessageSent={refresh}
            readOnly={oversight}
          />
        ) : (
          <div className="messages-empty">
            <i className="pi pi-comments" style={{ fontSize: "4rem", opacity: 0.3 }}></i>
            <p>Select a conversation to start chatting</p>
            <Button
              label="New Conversation"
              icon="pi pi-plus"
              onClick={() => setShowNewDialog(true)}
            />
          </div>
        )}
      </div>

      <NewConversationDialog
        visible={showNewDialog}
        onHide={() => setShowNewDialog(false)}
        onCreated={handleConversationCreated}
      />
    </div>
  );
};

export default MessagesPage;
